import { useSeoMeta } from '@unhead/react';
import { useNavigate } from 'react-router-dom';
import { useArchiveTagCounts } from '@/hooks/useArchiveTagCounts';
import { usePinnedNotes } from '@/hooks/usePinnedNotes';
import type { NostrEvent } from '@nostrify/nostrify';
import { MasonryGrid } from '@/components/MasonryGrid';
import { AppliedFilters, ArchiveSearch, FilterSheet } from '@/components/ArchiveFilterBar';
import { CollectionBar } from '@/components/CollectionBar';
import { FormatSwitch } from '@/components/FormatSwitch';
import { ContactLauncher } from '@/components/ContactLauncher';
import { COLLECTIONS } from '@/lib/taxonomy';
import { profileUrlFor } from '@/lib/archiveProfile';
import { ARCHIVE_PUBKEY } from '@/hooks/useArchiveNotes';
import { nip19 } from 'nostr-tools';
import { BackToTop } from '@/components/BackToTop';
import { InscriptionDirectory } from '@/components/InscriptionDirectory';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Archive, ArrowLeft } from 'lucide-react';
import {
  applyFilters,
  collectionCounts,
  hasFilters,
  indexNotes,
  termCounts,
  type ArchiveFilters,
} from '@/lib/archiveFilters';
import { INSCRIPTION_COUNT } from '@/lib/siteConfig';
import React from 'react';

const FORMAT_CHIPS = [
  { slug: 'image', label: 'Image' },
  { slug: 'video', label: 'Video' },
  { slug: 'pdf', label: 'Doc' },
  { slug: 'text', label: 'Text' },
] as const;

const footerLink =
  'font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/70 underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

const DISPLAY_PAGE_SIZE = 60;

function readStoredCount(key: string): number {
  try {
    const n = Number(sessionStorage.getItem(key));
    return Number.isFinite(n) && n >= DISPLAY_PAGE_SIZE ? n : DISPLAY_PAGE_SIZE;
  } catch {
    return DISPLAY_PAGE_SIZE;
  }
}

export function BitcoinPage() {
  useSeoMeta({
    title: 'Bitcoin Inscriptions — 9/11receipts',
    description:
      'Records inscribed directly onto the Bitcoin blockchain for permanent, censorship-resistant preservation.',
    ogTitle: 'Bitcoin Inscriptions — 9/11receipts',
    ogDescription:
      '9/11receipts holdings inscribed on the Bitcoin blockchain. Accessible through any Ordinals explorer.',
    twitterCard: 'summary',
  });

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = React.useState('');

  // Filter to only inscribed records
  const baseFilters: ArchiveFilters = {
    terms: [],
    collection: 'bitcoin',
    query: searchParams,
  };

  const { data: tagData, isLoading, isFetching, isError } = useArchiveTagCounts();
  const { data: pinnedNotes } = usePinnedNotes();

  const allNotes = (() => {
    const byId = new Map<string, NostrEvent>();
    for (const n of tagData?.notes ?? []) byId.set(n.id, n);
    for (const n of pinnedNotes ?? []) if (!byId.has(n.id)) byId.set(n.id, n);
    return Array.from(byId.values()).sort((a, b) => b.created_at - a.created_at);
  })();

  const indexed = indexNotes(allNotes);
  const filteredNotes = applyFilters(indexed, baseFilters);

  const countKey = `archive:visible:bitcoin`;
  const [visibleCount, setVisibleCount] = React.useState(() => readStoredCount(countKey));

  React.useEffect(() => {
    try {
      sessionStorage.setItem(countKey, String(visibleCount));
    } catch {
      /* non-fatal */
    }
  }, [countKey, visibleCount]);

  const notes = filteredNotes.slice(0, visibleCount);
  const hasMore = visibleCount < filteredNotes.length;

  const viewTotal = filteredNotes.length;
  const shownCount = notes.length;
  const showProgress = viewTotal > 0 && shownCount > 0;

  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!hasMore) return;
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount(c => c + DISPLAY_PAGE_SIZE);
        }
      },
      { rootMargin: '800px 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, filteredNotes.length]);

  const facetCounts = termCounts(indexed, baseFilters);

  return (
    <div className="min-h-screen bg-background">
      <a
        href="#bitcoin-results"
        className="fixed left-3 top-3 z-[100] -translate-y-20 rounded-sm bg-foreground px-4 py-2 font-engraved text-[11px] font-semibold uppercase tracking-[0.2em] text-background transition-transform focus-visible:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
      >
        Skip to the records
      </a>

      <div id="bitcoin-feed" className="scroll-mt-12">
        {/* Back button and title */}
        <div className="px-4 py-5 sm:py-6">
          <div className="mx-auto max-w-6xl">
             <button
               type="button"
               onClick={() => navigate('/', { state: { scrollTo: 'archive-feed' } })}
               className="inline-flex items-center gap-2 text-sm font-engraved uppercase tracking-[0.16em] text-foreground/70 hover:text-foreground transition-colors mb-4"
             >
               <ArrowLeft className="h-4 w-4" />
               Back to archive
             </button>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-2">
              Bitcoin Inscriptions
            </h1>
            <p className="font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/70 mb-6">
              On-chain provenance
            </p>
          </div>
        </div>

        {/* About section */}
        <div className="px-4 pb-6">
          <div className="mx-auto max-w-6xl">
            <div className="border-l-4 border-flag-red pl-4">
              <p className="font-display text-base italic text-foreground/70 mb-4">
                Records inscribed directly onto the Bitcoin blockchain, where they can never be edited or taken down.
              </p>
              <p className="text-sm text-foreground/80 leading-relaxed mb-4">
                The archive's complete holdings are preserved on the world's largest distributed ledger, accessible
                through any Ordinals explorer. This immutable, decentralized approach ensures that critical historical
                documentation can be accessed and verified by anyone, anywhere, at any time — without reliance on
                centralized servers or institutions.
              </p>
              <p className="text-sm text-foreground/80 leading-relaxed mb-4">
                <strong>{INSCRIPTION_COUNT} permanent on-chain inscriptions</strong> preserve the complete 9/11
                Receipts archive on Bitcoin.
              </p>
            </div>
          </div>
        </div>

        {/* The Archive & Directory manifest and the inscriptions it lists */}
        <InscriptionDirectory />

        {/* Toolbar */}
        <div className="px-4 pb-6">
          <div className="mx-auto max-w-6xl">
            <CollectionBar
              collections={COLLECTIONS}
              active="bitcoin"
              isDefault={false}
              cardsId="bitcoin-filter"
              counts={collectionCounts(indexed)}
              total={isLoading ? undefined : viewTotal}
              onSelect={() => {
                navigate('/');
              }}
              search={
                <ArchiveSearch
                  inline
                  value={searchParams}
                  onChange={setSearchParams}
                />
              }
              filters={
                <FilterSheet
                  inline
                  filters={baseFilters}
                  onChange={() => {}}
                  counts={facetCounts}
                  resultCount={filteredNotes.length}
                  loading={isLoading}
                />
              }
              formats={<FormatSwitch formats={FORMAT_CHIPS} activeFormat={null} onSelectFormat={() => {}} />}
            />
          </div>
        </div>

        {/* Records section */}
        <main aria-label="Bitcoin inscriptions" className="max-w-6xl mx-auto px-4 pb-8 pt-6">
          <div id="bitcoin-results" tabIndex={-1} className="scroll-mt-52 outline-none" />

          {isLoading && (
            <div className="flex gap-5 items-start">
              {[0, 1, 2].map(col => (
                <div key={col} className="flex-1 min-w-0 flex flex-col gap-5">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="rounded-2xl border border-foreground/20 overflow-hidden"
                    >
                      <Skeleton
                        className="w-full"
                        style={{ height: `${160 + (((col * 3 + i) * 53) % 160)}px` }}
                      />
                      <div className="p-4 space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {isError && !isLoading && (
            <div className="border border-dashed border-destructive rounded-3xl p-12 text-center">
              <Archive className="w-10 h-10 mx-auto mb-3 text-destructive/50" />
              <p className="font-typewriter text-sm uppercase tracking-wider text-muted-foreground">
                Unable to load inscriptions. Check your relay connections and try again.
              </p>
            </div>
          )}

          {!isLoading && !isError && notes.length === 0 && (
            <div className="border border-dashed border-foreground/30 rounded-3xl p-16 text-center">
              <Archive className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
              <p className="font-sans text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
                No inscribed records found. The relay may be syncing.
              </p>
            </div>
          )}

          {notes.length > 0 && <MasonryGrid notes={notes} />}

          {!isLoading && hasMore && (
            <div ref={loadMoreRef} className="flex flex-col items-center gap-4 pt-8 pb-12">
              <Button
                variant="outline"
                size="lg"
                onClick={() => setVisibleCount(c => c + DISPLAY_PAGE_SIZE)}
                className="gap-2 font-typewriter text-xs uppercase tracking-[0.2em] rounded-full border-foreground/40 px-8 hover:bg-foreground hover:text-background"
              >
                <Archive className="w-4 h-4" />
                Load more records
              </Button>
            </div>
          )}

          {!isLoading && !hasMore && notes.length > 0 && (
            <div className="pt-8 pb-12 flex items-center justify-center gap-3">
              <span className="h-px w-12 bg-foreground/30" />
              <p className="text-[11px] text-muted-foreground font-typewriter uppercase tracking-[0.25em]">
                End · {viewTotal.toLocaleString()} records
              </p>
              <span className="h-px w-12 bg-foreground/30" />
            </div>
          )}

          {/* Footer */}
          <footer className="border-t-2 border-foreground/70 pt-8 pb-12 mt-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-foreground/30" />
              <ContactLauncher>
                {(openContact) => (
                  <button
                    type="button"
                    onClick={openContact}
                    aria-label="9/11receipts — contact the archive"
                    className="flex items-center justify-center rounded-full border border-foreground/50 px-4 py-1.5 font-masthead text-sm leading-none text-foreground/80 transition-colors duration-200 [-webkit-tap-highlight-color:transparent] hover:border-foreground hover:bg-foreground hover:text-background active:border-foreground active:bg-foreground active:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    9/11receipts
                  </button>
                )}
              </ContactLauncher>
              <div className="h-px w-12 bg-foreground/30" />
            </div>
            <p className="font-typewriter text-[10px] text-muted-foreground uppercase tracking-[0.3em] mb-3">
              Censorship-Resistant Archive
            </p>
            <p className="text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
              Managed by 9/11 victim family members and survivors. All records sourced directly from the Nostr network
              and cryptographically signed.
            </p>

            <nav
              aria-label="Take part"
              className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2"
            >
              <a
                href={profileUrlFor(nip19.npubEncode(ARCHIVE_PUBKEY))}
                target="_blank"
                rel="noopener noreferrer"
                className={footerLink}
              >
                Engage on Nostr
              </a>
               <button
                 type="button"
                 onClick={() => navigate('/', { state: { scrollTo: 'archive-feed' } })}
                 className={footerLink}
               >
                 Back to archive
               </button>
              <ContactLauncher>
                {(openContact) => (
                  <button type="button" onClick={openContact} className={footerLink}>
                    Contact
                  </button>
                )}
              </ContactLauncher>
            </nav>

            <p
              aria-hidden="true"
              className="mt-6 select-none font-mono text-[10px] tracking-[0.2em] text-foreground/50"
            >
              -----END PUBLIC RECORD-----
            </p>
          </footer>
        </main>
      </div>

      <BackToTop />
    </div>
  );
}

export default BitcoinPage;
